import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useMemo } from 'react';
import { ChangePasswordForm, useSession } from '@/features/auth';
import { companyOptionsQuery } from '@/features/company-scope';
import { roleAssignments } from './role-assignments';

/** "Meu perfil": nome, perfis de acesso com as empresas de cada um e troca de senha. */
export function ProfilePage(): ReactNode {
  const session = useSession((state) => state.session);
  const organizationId = session?.organizationId ?? '';
  const canReadCompanies = session?.permissions.includes('companies:read') === true;
  const { data } = useQuery({
    ...companyOptionsQuery(organizationId),
    enabled: canReadCompanies && organizationId !== '',
    meta: { silent: true },
  });
  const names = useMemo(
    () =>
      new Map(
        (data?.items ?? []).map((company) => [company.id, company.tradeName ?? company.legalName]),
      ),
    [data],
  );
  const assignments = roleAssignments(session?.roles ?? []);

  return (
    <div className="space-y-8">
      <section aria-labelledby="profile-title" className="space-y-4">
        <h1 id="profile-title" className="text-2xl font-semibold">
          Meu perfil
        </h1>
        <dl className="grid max-w-xl gap-x-6 gap-y-2 text-sm sm:grid-cols-[10rem_1fr]">
          <dt className="text-muted-foreground">Nome</dt>
          <dd>{session?.displayName}</dd>
          <dt className="text-muted-foreground">Perfis de acesso</dt>
          <dd>
            <ul className="space-y-1">
              {assignments.map((assignment) => (
                <li key={assignment.roleName}>
                  <span className="font-medium">{assignment.roleName}</span>
                  {' — '}
                  {assignment.companyIds === null
                    ? 'todas as empresas'
                    : assignment.companyIds
                        .map((companyId) => names.get(companyId) ?? 'empresa sem acesso de leitura')
                        .join(', ')}
                </li>
              ))}
            </ul>
          </dd>
        </dl>
      </section>
      <ChangePasswordForm />
    </div>
  );
}
